import { CopilotStatus } from "@/services/copilot";
import { RecommendationType } from "@/services/recommendations/recommendations";
import { ResourceChannel } from "@/services/resources";
import type { Callout, Sitelink } from "@/services/ad_extensions";
import { AdGroup, Campaign } from "../marketing";
import { Ad } from "../ads";

export enum RecommendationTaskStatus {
  TO_DO = "To do",
  IN_PROGRESS = "In progress",
  DONE = "Done",
  VERIFIED = "Verified",
  DISMISSED = "Dismissed",
}

export interface BaseRecommendationTask {
  pk: number;
  createdAt: string;
  modifiedAt: string;
  assignee: number | null;
  status: RecommendationTaskStatus;
  externalId: string | null;
  copilotStatus: CopilotStatus | null;
  ad: Ad["pk"] | null;
  adGroup: AdGroup["pk"] | null;
  campaign: Campaign["pk"] | null;
}

export interface BaseResourceRecommendationTask extends BaseRecommendationTask {
  resource: number;
}

export interface BaseOrganizationRecommendationTask extends BaseRecommendationTask {
  resource: null;
}

export enum GoogleAdsCampaignServingStatus {
  UNSPECIFIED = "UNSPECIFIED",
  UNKNOWN = "UNKNOWN",
  SERVING = "SERVING",
  NONE = "NONE",
  PENDING = "PENDING",
  SUSPENDED = "SUSPENDED",
}

export interface GoogleAdsMaximumRecommendedAmountOfKeywordsPerAdGroupRecommendationDetails {
  campaignId: number;
  campaignName: string;
  campaignServingStatus?: GoogleAdsCampaignServingStatus;
  adGroupId: number;
  adGroupName: string;
  keywordCount: number;
}

export interface GoogleAdsMaximumRecommendedAmountOfKeywordsPerAdGroupRecommendation extends BaseResourceRecommendationTask {
  details: GoogleAdsMaximumRecommendedAmountOfKeywordsPerAdGroupRecommendationDetails;
}

export interface GoogleAdsMinimumAdsOfTypePerAdGroupRecommendationDetails {
  campaignId: number;
  campaignName: string;
  campaignServingStatus?: GoogleAdsCampaignServingStatus;
  adGroupId: number;
  adGroupName: string;
  adsOfType: { EXPANDED_TEXT_AD: number };
}

export interface GoogleAdsMinimumAdsOfTypePerAdGroupRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.GOOGLE_ADS_MINIMUM_ADS_OF_TYPE_PER_AD_GROUP;
  details: GoogleAdsMinimumAdsOfTypePerAdGroupRecommendationDetails;
}

export interface GoogleAdsOverlappingKeywordsRecommendationDetailsEntryMetrics {
  clicks: number;
  conversions: number;
  impressions: number;
  qualityScore: number;
  conversionsPerImpression: number;
}

export interface GoogleAdsOverlappingKeywordsRecommendationDetailsEntry {
  campaignId: number;
  campaignName: string;
  campaignServingStatus?: GoogleAdsCampaignServingStatus;
  criterionId: number;
  adGroupId: number;
  adGroupName: string;
  metrics: GoogleAdsOverlappingKeywordsRecommendationDetailsEntryMetrics;
}

export interface GoogleAdsOverlappingKeywordsRecommendationDetails {
  keywordText: string;
  keywordMatchType: string;
  entries: GoogleAdsOverlappingKeywordsRecommendationDetailsEntry[];
}

export interface GoogleAdsOverlappingKeywordsRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.GOOGLE_ADS_OVERLAPPING_KEYWORDS;
  details: GoogleAdsOverlappingKeywordsRecommendationDetails;
  externalId: string;
}

export interface GoogleAdsStrengthDiscoveryAdsRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.GOOGLE_ADS_DISCOVERY_ADS_IMPROVE_AD_STRENGTH;
  details: {
    adGroupName: string;
    discoveryAdType: string;
    discoveryResponsiveAdHeadlines: { text: string }[];
    discoveryResponsiveAdDescriptions: { text: string }[];
    campaignName: string;
  };
}
export interface GoogleAdsAdStrengthResponsiveSearchAdsRecommendationDetails {
  adGroupName: string;
  adGroupId: number;
  responsiveSearchAdHeadlines: { text: string }[];
  responsiveSearchAdDescriptions: { text: string }[];
  campaignName: string;
}

export interface GoogleAdsAdStrengthResponsiveSearchAdsRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.GOOGLE_ADS_RESPONSIVE_SEARCH_AD_IMPROVE_AD_STRENGTH;
  details: GoogleAdsAdStrengthResponsiveSearchAdsRecommendationDetails;
}

export interface GoogleAdsOverlappingKeywordsAdGroupMetrics {
  hit: GoogleAdsOverlappingKeywordsRecommendationDetails;
  bestPerformingAdGroupId: number;
}

export interface GoogleAdsNegativeSharedListRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.GOOGLE_ADS_SHARED_NEGATIVE_LIST;
  details: {
    id: string;
    name: string;
    isStarted: boolean;
    sharedNegativeListExists: boolean;
  };
}

export interface GoogleAdsBrandedGenericKeywordMixRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.GOOGLE_ADS_BRANDED_GENERIC_KEYWORD_MIX;
  details: {
    id: string;
    keyword: string;
    branded: boolean;
    otherKeywordTypeCount: number;
    campaign: { id: string; name: string };
  };
}

export interface FacebookAdSetSizeRecommendationRecommendationDetails {
  adset: { id: string; name: string; isStarted?: boolean };
  campaign: { id: string; name: string };
  adCount: number;
}

export interface FacebookAdSetSizeRecommendationRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.FACEBOOK_AD_SET_SIZE;
  details: FacebookAdSetSizeRecommendationRecommendationDetails;
}

export interface FacebookAdFrequencyRecommendationDetails {
  adsetId: string;
  adsetName: string;
  campaignId: string;
  campaignName: string;
  frequency: number;
}

export interface FacebookAdFrequencyRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.FACEBOOK_AD_FREQUENCY;
  details: FacebookAdFrequencyRecommendationDetails;
}

export enum FacebookMissingPixelInstallationStatus {
  MISSING = "MISSING",
  NOT_FIRED = "NOT_FIRED",
}

export interface FacebookMissingPixelInstallationRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.FACEBOOK_MISSING_PIXEL_INSTALLATION;
  details:
    | { status: FacebookMissingPixelInstallationStatus.MISSING; pixel?: null }
    | {
        status: FacebookMissingPixelInstallationStatus.NOT_FIRED;
        pixel?: { id: string; name: string };
      };
}

export interface FacebookMissingUtmTrackingRecommendationDetails {
  adIds: string[];
  adSet: { id: string; name: string; isStarted?: boolean };
  campaign: { id: string; name: string };
}

export interface FacebookMissingUtmTrackingRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.FACEBOOK_MISSING_UTM_TRACKING;
  details: FacebookMissingUtmTrackingRecommendationDetails;
}

export enum FacebookAdTextField {
  PRIMARY_TEXT = "PRIMARY_TEXT",
  HEADLINE = "HEADLINE",
  DESCRIPTION = "DESCRIPTION",
}

export interface FacebookAdTextLengthRecommendationDetails {
  ad: { id: string; name: string; tooLongTextFields: FacebookAdTextField[] };
  adSet: { id: string; name: string; isStarted?: boolean };
  campaign: { id: string; name: string };
}

export interface FacebookAdTextLengthRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.FACEBOOK_AD_TEXT_LENGTH;
  details: FacebookAdTextLengthRecommendationDetails;
}

export interface GoogleAdsCalloutAmountRecommendationDetailsExtension {
  type: "CALLOUT";
  details: Callout;
}

export interface GoogleAdsCalloutAmountRecommendationDetails {
  extensionCount: number;
  extensions: GoogleAdsCalloutAmountRecommendationDetailsExtension[];
  adGroup: { id: string; name: string; resourceName: string };
  campaign: {
    id: string;
    name: string;
    resourceName: string;
    servingStatus?: GoogleAdsCampaignServingStatus;
  };
}

export interface GoogleAdsCalloutAmountRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.GOOGLE_ADS_CALLOUT_AMOUNT;
  details: GoogleAdsCalloutAmountRecommendationDetails;
}

export interface GoogleAdsSitelinkAmountRecommendationDetailsExtension {
  type: "SITELINK";
  details: Sitelink;
}
export interface GoogleAdsSitelinkAmountRecommendationDetails {
  extensionCount: number;
  extensions: GoogleAdsSitelinkAmountRecommendationDetailsExtension[];
  adGroup: { id: string; name: string; resourceName: string };
  campaign: {
    id: string;
    name: string;
    resourceName: string;
    servingStatus?: GoogleAdsCampaignServingStatus;
  };
}

export interface GoogleAdsSitelinkAmountRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.GOOGLE_ADS_SITELINK_AMOUNT;
  details: GoogleAdsSitelinkAmountRecommendationDetails;
}

export interface GoogleAdsZeroImpressionKeywordsRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.GOOGLE_ADS_ZERO_IMPRESSION_KEYWORDS;
  details: { count: number };
}

export interface GoogleAdsBroadMatchTypeKeywordRecommendationDetails {
  adGroup: { id: string; name: string };
  campaign: {
    id: string;
    name: string;
  };
  id: string;
  text: string;
  matchType: string;
}

export interface GoogleAdsBroadMatchTypeKeywordRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.GOOGLE_ADS_BROAD_MATCH_TYPE_KEYWORD;
  details: GoogleAdsBroadMatchTypeKeywordRecommendationDetails;
}

export interface GoogleAdsConversionActionsRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.GOOGLE_ADS_CONVERSION_ACTIONS;
  details: Record<string, never>;
}

export interface GoogleAdsExcludeIrrelevantSearchTermsRecommendationDetailsV1 {
  version: undefined;
  adGroup: { id: string; name: string };
  campaign: {
    id: string;
    name: string;
    servingStatus: string;
  };
  keywords: { text: string; matchType: string }[];
  searchTerm: string;
}

export interface GoogleAdsExcludeIrrelevantSearchTermsRecommendationDetailsV2 {
  version: "v2";
  keyword: string;
  campaigns: {
    id: string;
    name: string;
  }[];
}

export interface GoogleAdsExcludeIrrelevantSearchTermsRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.GOOGLE_ADS_EXCLUDE_IRRELEVANT_SEARCH_TERMS;
  details:
    | GoogleAdsExcludeIrrelevantSearchTermsRecommendationDetailsV1
    | GoogleAdsExcludeIrrelevantSearchTermsRecommendationDetailsV2;
}

export interface GoogleAdsLimitedByBudgetRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.GOOGLE_ADS_LIMITED_BY_BUDGET;
  details: { id: string; name: string; lostImpressionShare: number };
}

export interface LinkedInAdsCampaignSizeRecommendationDetails {
  campaign: { id: string; name: string; isStarted: boolean };
  campaignGroup: { id: string; name: string };
  adCount: number;
}

export interface LinkedInAdsCampaignSizeRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.LINKED_IN_ADS_CAMPAIGN_SIZE;
  details: LinkedInAdsCampaignSizeRecommendationDetails;
}

export interface LinkedInAdsTargetingForSmallAudienceRecommendationDetails {
  campaign: { id: string; name: string; isStarted: boolean };
  campaignGroup: { id: string; name: string };
}

export interface LinkedInAdsTargetingForSmallAudienceRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.LINKED_IN_ADS_TARGETING_FOR_SMALL_AUDIENCE;
  details: LinkedInAdsTargetingForSmallAudienceRecommendationDetails;
}

export interface LinkedInAdsUrlInAdTextRecommendationDetails {
  campaign: { id: string; name: string; isStarted: boolean };
  creative: { id: string; name: string };
}

export interface LinkedInAdsUrlInAdTextRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.LINKED_IN_ADS_URL_IN_AD_TEXT;
  details: LinkedInAdsUrlInAdTextRecommendationDetails;
}

export enum LinkedInAdsTextField {
  INTRODUCTORY_TEXT = "INTRODUCTORY_TEXT",
  HEADLINE = "HEADLINE",
}

export interface LinkedInAdsTextLengthRecommendationDetails {
  ad: { id: string; tooLongTextFields: LinkedInAdsTextField[] };
  campaign: { id: string; name: string; isStarted?: boolean };
  campaignGroup: { id: string; name: string };
}

export interface LinkedInAdsTextLengthRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.LINKED_IN_ADS_TEXT_LENGTH;
  details: LinkedInAdsTextLengthRecommendationDetails;
}

export interface LinkedInAdsCampaignConversionsRecommendationDetails {
  campaign: { id: string; name: string; isStarted: boolean };
  campaignGroup: { id: string; name: string };
}

export interface LinkedInAdsCampaignConversionsRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.LINKED_IN_ADS_CAMPAIGN_CONVERSIONS;
  details: LinkedInAdsCampaignConversionsRecommendationDetails;
}

export interface GoogleAnalytics4TrackingCodeRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.GOOGLE_ANALYTICS_4_TRACKING_CODE;
  details: Record<string, never>;
}

export interface BaseSEOIssueRecommendationTask extends BaseOrganizationRecommendationTask {
  externalId: string;
  page: number;
}

export interface SEOTooLongTitleIssueRecommendationDetails {
  subtype: "TITLE_TOO_LONG";
  title: string;
}

export interface SEOTooShortTitleIssueRecommendationDetails {
  subtype: "TITLE_TOO_SHORT";
  title: string;
}

export interface SEODuplicateTitleIssueRecommendationDetails {
  subtype: "TITLE_DUPLICATE";
  title: string;
  count: number;
}

export interface SEOAllCapsTitleIssueRecommendationDetails {
  subtype: "TITLE_ALL_CAPS";
  title: string;
}

export interface SEOMissingTitleIssueRecommendationDetails {
  subtype: "TITLE_MISSING";
}

export interface SEOTitleIssueRecommendation extends BaseSEOIssueRecommendationTask {
  recommendation: RecommendationType.SEO_TITLE_ISSUE;
  details:
    | SEOTooLongTitleIssueRecommendationDetails
    | SEOTooShortTitleIssueRecommendationDetails
    | SEODuplicateTitleIssueRecommendationDetails
    | SEOAllCapsTitleIssueRecommendationDetails
    | SEOMissingTitleIssueRecommendationDetails;
}

export interface SEOTooLongDescriptionIssueRecommendationDetails {
  subtype: "DESCRIPTION_TOO_LONG";
  description: string;
}

export interface SEOTooShortDescriptionIssueRecommendationDetails {
  subtype: "DESCRIPTION_TOO_SHORT";
  description: string;
}

export interface SEODuplicateDescriptionIssueRecommendationDetails {
  subtype: "DESCRIPTION_DUPLICATE";
  title: string;
  count: number;
}

export interface SEOMissingDescriptionIssueRecommendationDetails {
  subtype: "DESCRIPTION_MISSING";
}

export interface SEODescriptionIssueRecommendation extends BaseSEOIssueRecommendationTask {
  recommendation: RecommendationType.SEO_DESCRIPTION_ISSUE;
  pageHasTextContent: boolean;
  details:
    | SEOTooLongDescriptionIssueRecommendationDetails
    | SEOTooShortDescriptionIssueRecommendationDetails
    | SEODuplicateDescriptionIssueRecommendationDetails
    | SEOMissingDescriptionIssueRecommendationDetails;
}

export interface SEODuplicateHeadingIssueRecommendationDetails {
  subtype: "HEADING_DUPLICATE";
  text: string;
  tags: string[];
}

export interface SEOMissingH1HeadingIssueRecommendationDetails {
  subtype: "HEADING_H1_MISSING";
}

export interface SEOHeadingIssueRecommendation extends BaseSEOIssueRecommendationTask {
  recommendation: RecommendationType.SEO_HEADING_ISSUE;
  details:
    | SEODuplicateHeadingIssueRecommendationDetails
    | SEOMissingH1HeadingIssueRecommendationDetails;
}

export interface SEONoIndexIndexabilityIssueRecommendationDetails {
  subtype: "INDEXABILITY_NO_INDEX";
  tag: string;
}

export interface SEOIndexabilityIssueRecommendation extends BaseSEOIssueRecommendationTask {
  recommendation: RecommendationType.SEO_INDEXABILITY_ISSUE;
  details: SEONoIndexIndexabilityIssueRecommendationDetails;
}

export interface SEOOpenGraphIssueRecommendation extends BaseSEOIssueRecommendationTask {
  recommendation: RecommendationType.SEO_OPEN_GRAPH_ISSUE;
  details: {
    subtype:
      | "OPEN_GRAPH_MISSING_URL"
      | "OPEN_GRAPH_MISSING_TITLE"
      | "OPEN_GRAPH_MISSING_DESCRIPTION"
      | "OPEN_GRAPH_MISSING_IMAGE";
  };
}

export interface SEOMissingAltTextImageIssueRecommendationDetails {
  subtype: "IMAGE_ALT_TEXT_MISSING";
  src: string;
}

export interface SEOTooLongAltTextImageIssueRecommendationDetails {
  subtype: "IMAGE_ALT_TEXT_TOO_LONG";
  src: string;
  altText: string;
}

export interface SEOImageIssueRecommendation extends BaseSEOIssueRecommendationTask {
  recommendation: RecommendationType.SEO_IMAGE_ISSUE;
  details:
    | SEOMissingAltTextImageIssueRecommendationDetails
    | SEOTooLongAltTextImageIssueRecommendationDetails;
}
export interface SEORedirectTooManyRedirectIssueRecommendationDetails {
  subtype: "REDIRECT_TOO_MANY";
  anchorText: string;
  url: string;
  destination: string;
  count: number;
  type: "internal" | "external";
}

export interface SEORedirectTooManySharedRedirectIssueRecommendationDetails extends Omit<
  SEORedirectTooManyRedirectIssueRecommendationDetails,
  "subtype"
> {
  subtype: "REDIRECT_TOO_MANY_SHARED";
  linkedFromCount: number;
  linkedFromExamples: string[];
}

export interface SEORedirectIssueRecommendation extends Omit<
  BaseSEOIssueRecommendationTask,
  "page"
> {
  recommendation: RecommendationType.SEO_REDIRECT_ISSUE;
  page: number | null;
  details:
    | SEORedirectTooManyRedirectIssueRecommendationDetails
    | SEORedirectTooManySharedRedirectIssueRecommendationDetails;
}

export interface SEOBrokenLinkDetails {
  subtype: "LINK_BROKEN";
  version: undefined;
  url: string;
  type: "internal" | "external";
  status: number;
  anchorText: string;
}

export interface SEOBrokenLinkDetailsV2 {
  subtype: "LINK_BROKEN";
  version: "v2";
  links: {
    url: string;
    type: "internal" | "external";
    status: number;
    anchorText: string;
  }[];
}

// One broken link on several pages; its externalId is the link, not a page.
export interface SEOBrokenLinkSharedDetails {
  subtype: "LINK_BROKEN_SHARED";
  version: "v2";
  links: {
    url: string;
    type: "internal" | "external";
    status: number;
    anchorText: string;
    linkedFromCount: number;
    // The shallowest pages linking it, at most 50.
    linkedFromExamples: string[];
  }[];
}

export interface SEOLinkIssueRecommendation extends Omit<
  BaseSEOIssueRecommendationTask,
  "page"
> {
  recommendation: RecommendationType.SEO_LINK_ISSUE;
  page: number | null;
  details:
    | SEOBrokenLinkDetails
    | SEOBrokenLinkDetailsV2
    | SEOBrokenLinkSharedDetails;
}

export interface SEOCanonicalLinkDetails {
  subtype: "CANONICAL_LINK";
  url: string;
  anchorText: string;
  canonical: string;
}

export interface SEOCanonicalLinkSharedDetails extends Omit<
  SEOCanonicalLinkDetails,
  "subtype"
> {
  subtype: "CANONICAL_LINK_SHARED";
  linkedFromCount: number;
  linkedFromExamples: string[];
}

export interface SEOCanonicalIssueRecommendation extends Omit<
  BaseSEOIssueRecommendationTask,
  "page"
> {
  recommendation: RecommendationType.SEO_CANONICAL_ISSUE;
  page: number | null;
  details: SEOCanonicalLinkDetails | SEOCanonicalLinkSharedDetails;
}

export interface SEOTextIssueRecommendation extends BaseSEOIssueRecommendationTask {
  recommendation: RecommendationType.SEO_TEXT_ISSUE;
  details: { subtype: "TEXT_TOO_LOW_WORD_COUNT"; count: number };
}

export interface ActiveSocialMediaAccountRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.ACTIVE_SOCIAL_MEDIA_ACCOUNT;
  details: {
    channel:
      | ResourceChannel.FACEBOOK_ORGANIC
      | ResourceChannel.LINKED_IN_ORGANIC
      | ResourceChannel.INSTAGRAM_ORGANIC
      | ResourceChannel.YOUTUBE
      | ResourceChannel.TIK_TOK_ORGANIC;
  };
}

export interface AddressPolicyViolationsRecommendation extends BaseRecommendationTask {
  recommendation: RecommendationType.ADDRESS_POLICY_VIOLATIONS;
  details: {
    channel: ResourceChannel;
    type: string;
    details: Record<string, PolicyViolationThing>;
    related: Record<string, PolicyViolationThing>;
  };
}

export interface PolicyViolationThing {
  // Don't have a complete overview of possible object structures here,
  // so expand as we notice anything missing over time

  // If ad, has nested ad object
  ad?: {
    id: string;
    type_: string;
    resourceName: string;
  };
  // If non-ad, has root id
  id?: string;
  status?: string;
  resourceName: string;
  name?: string;
  policySummary?: {
    reviewStatus: string;
    approvalStatus: string;
    policyTopicEntries: PolicyTopicEntry[];
  };
}

export interface PolicyTopicEntry {
  // Don't have a complete overview of possible object structures here,
  // so expand as we notice anything missing over time
  topic: string;
  type_: string;
  evidences?: PolicyTopicEntryEvidence[];
  constraints?: [
    {
      resellerConstraints?: object;
    },
  ];
}

export interface PolicyTopicEntryEvidence {
  textList?: {
    texts?: string[];
  };
}

export type DeadLinkCheckerRecommendationDetails = (
  | {
      campaignGroup: { id: string; name: string };
      campaign: { id: string; name: string };
      id: string;
      deadLinks: { url: string; status: number }[];
      channel: ResourceChannel.LINKED_IN_ADS;
    }
  | {
      campaign: { id: string; name: string };
      adSet: { id: string; name: string };
      id: string;
      name: string;
      deadLinks: { url: string; status: number }[];
      channel: ResourceChannel.FACEBOOK_ADS;
    }
  | {
      campaignGroup: { id: string; name: string };
      adGroup: { id: string; name: string };
      id: string;
      deadLinks: { url: string; status: number }[];
      channel: ResourceChannel.GOOGLE_ADS;
    }
  | {
      id: string;
      name: string;
      deadLinks: { url: string; status: number }[];
      channel: ResourceChannel.XANDR;
    }
  | {
      campaign: { id: string; name: string };
      adSquad: { id: string; name: string };
      id: string;
      name: string;
      deadLinks: { url: string; status: number }[];
      channel: ResourceChannel.SNAPCHAT_ADS;
    }
  | {
      campaign: { id: string; name: string };
      adGroup: { id: string; name: string };
      id: string;
      deadLinks: { url: string; status: number }[];
      channel: ResourceChannel.BING_ADS;
    }
  | {
      campaign: { id: string; name: string };
      adGroup: { id: string; name: string };
      id: string;
      name: string;
      deadLinks: { url: string; status: number }[];
      channel: ResourceChannel.TIK_TOK_ADS;
    }
) & { action?: "ENABLE" | "PAUSE" };

export interface DeadLinkCheckerRecommendation extends BaseResourceRecommendationTask {
  recommendation: RecommendationType.DEAD_LINK_CHECKER;
  details: DeadLinkCheckerRecommendationDetails;
}

export interface RenewSSLCertificateRecommendation extends BaseOrganizationRecommendationTask {
  recommendation: RecommendationType.RENEW_SSL_CERTIFICATE;
  details: { expiry: string; domain: string };
}

export interface Soft404Recommendation extends BaseOrganizationRecommendationTask {
  recommendation: RecommendationType.SOFT_404;
  details: { domain: string };
}

// AI suggestion steps don't carry the parent's DISMISSED state — the
// parent task's status is rolled up from these step transitions, and a
// dismissed parent has no step writes. Verified is also parent-only.
export type AISuggestionStepStatus =
  | RecommendationTaskStatus.TO_DO
  | RecommendationTaskStatus.IN_PROGRESS
  | RecommendationTaskStatus.DONE;

export interface AISuggestionPersistedStep {
  text: string;
  status: AISuggestionStepStatus;
  assigneeId?: number | null;
  startedAt?: string | null;
  assignedAt?: string | null;
  // Set on the Done transition. The backend uses this both as the
  // completion-time stamp and as the "is this step closed?" signal
  // for ownership-preservation rules; the frontend reads it for
  // history display in the slideover.
  completedAt?: string | null;
}

export interface AISuggestionTaskStep {
  title: string;
  status: AISuggestionStepStatus;
  assignee: number | null;
  startedAt?: string | null;
  assignedAt?: string | null;
  completedAt?: string | null;
}

export function persistedAIStepToTaskStep(
  step: AISuggestionPersistedStep,
): AISuggestionTaskStep {
  // Older `details.aiSteps` rows (created before the write-side cleanup
  // in `taskAIStepToPersistedStep` landed) can carry leftover
  // `assignedAt`/`completedAt` even when the step is unassigned or not
  // Done. Drop those on read so the slideover and history UI don't
  // render ghost assignment/completion timestamps until the task is
  // edited and round-tripped through the cleaned writer.
  return {
    title: step.text,
    status: step.status,
    assignee: step.assigneeId ?? null,
    startedAt: step.startedAt,
    assignedAt:
      step.assigneeId !== null && step.assigneeId !== undefined
        ? step.assignedAt
        : null,
    completedAt:
      step.status === RecommendationTaskStatus.DONE ? step.completedAt : null,
  };
}

export function taskAIStepToPersistedStep(
  step: AISuggestionTaskStep,
): AISuggestionPersistedStep {
  // Only persist assignment metadata when the step actually has an
  // assignee — otherwise a frontend unassign would round-trip back into
  // `details.aiSteps` as "unassigned but still carries assignment
  // history", contradicting the backend rule that an open unassigned
  // step has no assignee_id / assigned_at fields at all.
  return {
    text: step.title,
    status: step.status,
    // Mirror `completedAt` below: the backend pops `started_at` on
    // TODO transitions (see `_apply_status_to_user_steps`), so a
    // reopened step that still carried its old `startedAt` would
    // round-trip back into `details.aiSteps` as already-started.
    startedAt:
      step.status === RecommendationTaskStatus.TO_DO ? null : step.startedAt,
    // Only carry `completedAt` when the step is actually Done. The
    // backend treats `completedAt` as the closed-step signal for AI
    // steps, so a reopened step that still has the old timestamp would
    // round-trip back into `details.aiSteps` as still-closed.
    ...(step.status === RecommendationTaskStatus.DONE
      ? { completedAt: step.completedAt }
      : { completedAt: null }),
    ...(step.assignee !== null
      ? {
          assigneeId: step.assignee,
          assignedAt: step.assignedAt,
        }
      : {}),
  };
}

export interface AISuggestionRecommendationDetails {
  aiTitle: string;
  aiDescription: string;
  aiCategory: string;
  aiPriority: string;
  aiSteps: AISuggestionPersistedStep[];
  aiSuggestionId: number;
  tag: string;
  impactSummary: string;
  [key: string]: unknown;
}

export interface AISuggestionRecommendation extends BaseOrganizationRecommendationTask {
  recommendation: RecommendationType.AI_SUGGESTION;
  details: AISuggestionRecommendationDetails;
}

export type RecommendationTask =
  | GoogleAdsMinimumAdsOfTypePerAdGroupRecommendation
  | GoogleAdsOverlappingKeywordsRecommendation
  | GoogleAdsAdStrengthResponsiveSearchAdsRecommendation
  | GoogleAdsBroadMatchTypeKeywordRecommendation
  | GoogleAdsStrengthDiscoveryAdsRecommendation
  | GoogleAdsCalloutAmountRecommendation
  | GoogleAdsSitelinkAmountRecommendation
  | GoogleAdsZeroImpressionKeywordsRecommendation
  | GoogleAdsExcludeIrrelevantSearchTermsRecommendation
  | GoogleAdsBrandedGenericKeywordMixRecommendation
  | FacebookAdSetSizeRecommendationRecommendation
  | FacebookAdFrequencyRecommendation
  | FacebookMissingPixelInstallationRecommendation
  | FacebookMissingUtmTrackingRecommendation
  | FacebookAdTextLengthRecommendation
  | LinkedInAdsCampaignSizeRecommendation
  | LinkedInAdsTargetingForSmallAudienceRecommendation
  | LinkedInAdsUrlInAdTextRecommendation
  | SEOTitleIssueRecommendation
  | SEODescriptionIssueRecommendation
  | SEOHeadingIssueRecommendation
  | SEOIndexabilityIssueRecommendation
  | SEOOpenGraphIssueRecommendation
  | SEOImageIssueRecommendation
  | SEORedirectIssueRecommendation
  | SEOLinkIssueRecommendation
  | DeadLinkCheckerRecommendation
  | RenewSSLCertificateRecommendation
  | Soft404Recommendation
  | AddressPolicyViolationsRecommendation
  | AISuggestionRecommendation;
